// User presence pings. Fills the activity gap where bitácora is silent —
// i.e. the user is logged in and "around" but hasn't made a change that
// produces a bitácora entry.
//
// Storage: top-level `userActivity` collection. One document per
// (user, UTC-hour) bucket. Document ID is deterministic so multi-tab and
// retried writes coalesce to a single document per hour, keeping cost low.
//
// Doc shape:
//   userId, userEmail, userName, role
//   venueId       — venue the user had selected when the ping fired
//   bucketHour    — "YYYY-MM-DDTHH" in UTC. The doc's natural start-of-hour
//                   timestamp; lets us query/sort without parsing the ID.
//   lastSeen      — serverTimestamp, updated on every ping
//   pings         — counter, +1 per ping (saturation hint within the hour)

import {
  doc,
  setDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db } from "../firebase";

// UTC hour bucket, e.g. "2026-05-11T17".
export const hourBucketUTC = (date = new Date()) => {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const hh = String(date.getUTCHours()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}`;
};

export const presenceDocId = (userId, bucket) => `${userId}_${bucket}`;

// Fire-and-forget presence ping. Skips admins (same convention as bitácora —
// admin browsing isn't "venue activity" we want to count).
export function logPresence({ user, userInfo, venueId }) {
  if (!user?.uid) return;
  if (userInfo?.role === "admin") return;

  const bucket = hourBucketUTC();
  const id = presenceDocId(user.uid, bucket);
  const ref = doc(db, "userActivity", id);

  setDoc(
    ref,
    {
      userId: user.uid,
      userEmail: user.email || userInfo?.email || "",
      userName: userInfo?.name || "",
      role: userInfo?.role || "",
      venueId: venueId || null,
      bucketHour: bucket,
      lastSeen: serverTimestamp(),
      pings: increment(1),
    },
    { merge: true }
  ).catch((err) => {
    console.warn("presence ping failed:", err);
  });
}
